import { useEffect, useState } from "react";
import {
  Building2,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Phone,
  User,
  Users,
  GraduationCap,
  Backpack,
  MessageSquare,
  Loader2,
  XCircle,
  CheckCircle2,
  AlertTriangle,
  UserX,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  BookingShare,
  Button,
  DialogHeader,
  DialogTitle,
} from "@sportscanner/ui";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "@/components/responsive-dialog";
import { apiGet, apiPost, getApiErrorCode } from "@/lib/api";
import { dateStrToEntry, durationLabel, formatRSD } from "./helpers";
import RescheduleStep from "./reschedule-step";

interface BookingDetail {
  id: string;
  bookingRef: string;
  accessToken: string;
  reservationUrl: string;
  date: string;
  startTime: string;
  endTime: string;
  status: "BOOKED" | "CANCELLED";
  seriesId: string | null;
  playerName: string;
  playerPhone: string;
  playerEmail: string | null;
  comment: string | null;
  // null name => booked / cancelled by the guest, not an admin user.
  bookedByUserName: string | null;
  cancelledByUserName: string | null;
  cancelledAt: string | null;
  noShow: boolean;
  noShowAt: string | null;
  noShowByUserName: string | null;
  // Past no-shows by this phone at this club, excluding the current booking.
  pastNoShowCount: number;
  courtId: string;
  courtName: string;
  courtIsIndoor: boolean;
  pricePerSession: string;
  sportId: string;
  sportName: string;
  sessionDurationMinutes: number;
  // Set for a coached session — the coach's name.
  coachName: string | null;
  clubName: string;
  createdAt: string;
  // Additional named players on the booking, ordered by `position`. For
  // tennis/padel: length 1 = singles (opponent), length 3 = doubles
  // (partner + 2 opponents). Other sports: free-text list.
  players: { name: string; position: number }[];
  // Rented equipment lines on the booking (read-only here).
  gear?: { name: string; quantity: number; unitPrice: string }[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clubId: string;
  // Free clubs don't charge — hide the price row.
  isFree: boolean;
  bookingId: string | null;
  canCancel: boolean;
  canEdit: boolean;
  onCancelled?: () => void;
  onRescheduled?: () => void;
}

export default function BookingDetailsDialog({
  open,
  onOpenChange,
  clubId,
  isFree,
  bookingId,
  canCancel,
  canEdit,
  onCancelled,
  onRescheduled,
}: Props) {
  const { t, i18n } = useTranslation();
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [confirmCancelSeriesOpen, setConfirmCancelSeriesOpen] = useState(false);
  const [view, setView] = useState<"details" | "reschedule">("details");

  // Reset back to the details view whenever the dialog (re)opens so reopening
  // a booking after moving another one doesn't land you on the reschedule step.
  useEffect(() => {
    if (open) setView("details");
  }, [open]);

  const query = useQuery({
    queryKey: ["admin", "booking", clubId, bookingId],
    queryFn: () =>
      apiGet<{ booking: BookingDetail }>(
        `/api/admin/clubs/${clubId}/bookings/${bookingId}`,
      ),
    enabled: Boolean(open && bookingId),
  });

  const cancelMutation = useMutation({
    mutationFn: () =>
      apiPost(`/api/admin/clubs/${clubId}/bookings/${bookingId}/cancel`, {}),
    onSuccess: () => {
      toast.success(t("bookings.details.cancelledToast"));
      setConfirmCancelOpen(false);
      onCancelled?.();
      void query.refetch();
    },
    onError: (err: unknown) => {
      toast.error(t(`errors.api.${getApiErrorCode(err)}`));
    },
  });

  const noShowMutation = useMutation({
    mutationFn: (next: boolean) =>
      apiPost<{ ok: true; noShow: boolean }>(
        `/api/admin/clubs/${clubId}/bookings/${bookingId}/no-show`,
        { noShow: next },
      ),
    onSuccess: (data) => {
      toast.success(
        t(
          data.noShow
            ? "bookings.details.noShowToastOn"
            : "bookings.details.noShowToastOff",
        ),
      );
      onCancelled?.();
      void query.refetch();
    },
    onError: (err: unknown) => {
      toast.error(t(`errors.api.${getApiErrorCode(err)}`));
    },
  });

  const cancelSeriesMutation = useMutation({
    mutationFn: (seriesId: string) =>
      apiPost<{ ok: true; cancelled: number; fromDate: string }>(
        `/api/admin/clubs/${clubId}/bookings/series/${seriesId}/cancel`,
        {},
      ),
    onSuccess: (data) => {
      toast.success(
        t("bookings.details.cancelSeriesToast", { count: data.cancelled }),
      );
      setConfirmCancelSeriesOpen(false);
      onCancelled?.();
      void query.refetch();
    },
    onError: (err: unknown) => {
      toast.error(t(`errors.api.${getApiErrorCode(err)}`));
    },
  });

  const booking = query.data?.booking;
  const dateEntry = booking ? dateStrToEntry(booking.date) : null;
  const formattedDate = dateEntry
    ? `${t(`days.long.${dateEntry.dayKey}`)}, ${dateEntry.date}. ${t(`months.${dateEntry.monthKey}`)}`
    : "";

  const dateLocale =
    i18n.language === "ru"
      ? "ru-RU"
      : i18n.language === "en"
        ? "en-US"
        : "sr-Latn-RS";
  const formatTimestamp = (iso: string) =>
    new Date(iso).toLocaleString(dateLocale, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  const formattedCreated = booking ? formatTimestamp(booking.createdAt) : "";
  const formattedCancelled = booking?.cancelledAt
    ? formatTimestamp(booking.cancelledAt)
    : "";
  const formattedNoShow = booking?.noShowAt
    ? formatTimestamp(booking.noShowAt)
    : "";

  return (
    <>
      <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
        <ResponsiveDialogContent className="md:max-w-md md:max-h-[90vh] md:overflow-y-auto">
          {view === "reschedule" && canEdit && booking ? (
            <RescheduleStep
              clubId={clubId}
              bookingId={booking.id}
              sportId={booking.sportId}
              currentCourtId={booking.courtId}
              currentDate={booking.date}
              currentStartTime={booking.startTime}
              onBack={() => setView("details")}
              onRescheduled={() => {
                onRescheduled?.();
                void query.refetch();
                setView("details");
              }}
            />
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>{t("bookings.details.title")}</DialogTitle>
              </DialogHeader>

              {query.isLoading ? (
                <div className="py-10 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
                </div>
              ) : !booking ? (
                <div className="py-10 text-center">
                  <p className="text-sm text-neutral-500">
                    {t("errors.api.NOT_FOUND")}
                  </p>
                </div>
              ) : (
                <>
                  {/* Status banner */}
                  {booking.status === "CANCELLED" ? (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
                      <XCircle className="w-4 h-4" />
                      {t("bookings.details.cancelled")}
                    </div>
                  ) : booking.noShow ? (
                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-700">
                      <UserX className="w-4 h-4" />
                      {t("bookings.details.noShowBadge")}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm text-emerald-700">
                      <CheckCircle2 className="w-4 h-4" />
                      {t("bookings.status.BOOKED")}
                    </div>
                  )}

                  {/* Booking ref + share */}
                  <div className="py-2 border-b border-neutral-200">
                    {booking.status === "BOOKED" ? (
                      <BookingShare
                        bookingRef={booking.bookingRef}
                        reservationUrl={booking.reservationUrl}
                        shareMessage={t("bookings.details.shareMessage", {
                          clubName: booking.clubName,
                          date: formattedDate,
                          time: booking.startTime,
                          url: booking.reservationUrl,
                        })}
                        shareTitle={booking.clubName}
                        refLabel={t("bookings.details.ref")}
                        shareLabel={t("bookings.details.share")}
                        copiedLabel={t("bookings.details.linkCopied")}
                        onCopied={() =>
                          toast.success(t("bookings.details.linkCopied"))
                        }
                        onCopyError={() =>
                          toast.error(t("bookings.details.copyError"))
                        }
                      />
                    ) : (
                      <div className="text-center space-y-2">
                        <p className="text-xs text-neutral-500">
                          {t("bookings.details.ref")}
                        </p>
                        <p className="font-mono text-lg font-bold text-neutral-800">
                          {booking.bookingRef}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div className="space-y-3">
                    <DetailRow
                      icon={<Building2 className="w-4 h-4 text-primary-500" />}
                      label={t("bookings.details.club")}
                      value={booking.clubName}
                    />
                    <DetailRow
                      icon={
                        <CalendarIcon className="w-4 h-4 text-primary-500" />
                      }
                      label={t("bookings.details.date")}
                      value={formattedDate}
                    />
                    <DetailRow
                      icon={<Clock className="w-4 h-4 text-primary-500" />}
                      label={t("bookings.details.time")}
                      value={`${booking.startTime} – ${booking.endTime} · ${durationLabel(booking.sessionDurationMinutes)}`}
                    />
                    <DetailRow
                      icon={<MapPin className="w-4 h-4 text-primary-500" />}
                      label={t("bookings.details.court")}
                      value={`${booking.courtName} · ${t(`sports.${booking.sportName}`)}`}
                    />
                    {booking.coachName && (
                      <DetailRow
                        icon={
                          <GraduationCap className="w-4 h-4 text-primary-500" />
                        }
                        label={t("bookings.details.coach")}
                        value={booking.coachName}
                      />
                    )}
                    {booking.gear && booking.gear.length > 0 && (
                      <DetailRow
                        icon={<Backpack className="w-4 h-4 text-primary-500" />}
                        label={t("gear.catalogueTitle")}
                        value={booking.gear
                          .map((g) => `${g.name} × ${g.quantity}`)
                          .join(", ")}
                      />
                    )}
                    <DetailRow
                      icon={<User className="w-4 h-4 text-primary-500" />}
                      label={t("bookings.details.player")}
                      value={booking.playerName}
                    />
                    <DetailRow
                      icon={<Phone className="w-4 h-4 text-primary-500" />}
                      label={t("bookings.details.playerPhone")}
                      value={booking.playerPhone}
                    />
                    {booking.pastNoShowCount > 0 && (
                      <div className="flex items-start gap-3 -mt-1">
                        <div className="mt-0.5">
                          <AlertTriangle className="w-4 h-4 text-amber-500" />
                        </div>
                        <p className="flex-1 text-sm font-medium text-amber-700">
                          {t("bookings.details.pastNoShows", {
                            count: booking.pastNoShowCount,
                          })}
                        </p>
                      </div>
                    )}
                    {booking.players.length > 0 && (
                      <RosterRow
                        players={booking.players}
                        sportName={booking.sportName}
                      />
                    )}
                    {booking.comment && (
                      <DetailRow
                        icon={
                          <MessageSquare className="w-4 h-4 text-primary-500" />
                        }
                        label={t("bookings.details.comment")}
                        value={booking.comment}
                      />
                    )}
                  </div>

                  {/* Price */}
                  {!isFree && (
                    <div className="flex items-center justify-between pt-3 border-t border-neutral-200">
                      <span className="text-sm text-neutral-500">
                        {t("bookings.details.price")}
                      </span>
                      <span className="font-heading font-bold text-neutral-800">
                        {formatRSD(
                          booking.pricePerSession,
                          t("bookings.details.free"),
                        )}
                      </span>
                    </div>
                  )}

                  {/* Footer meta */}
                  <div className="text-xs text-neutral-400 space-y-0.5">
                    <p>
                      {booking.bookedByUserName
                        ? t("bookings.details.reservedByAdmin", {
                            name: booking.bookedByUserName,
                          })
                        : t("bookings.details.reservedByGuest")}{" "}
                      · {formattedCreated}
                    </p>
                    {booking.status === "CANCELLED" && (
                      <p>
                        {booking.cancelledByUserName
                          ? t("bookings.details.cancelledByAdmin", {
                              name: booking.cancelledByUserName,
                            })
                          : t("bookings.details.cancelledByGuest")}
                        {formattedCancelled ? ` · ${formattedCancelled}` : ""}
                      </p>
                    )}
                    {booking.noShow && (
                      <p>
                        {booking.noShowByUserName
                          ? t("bookings.details.noShowFlaggedAt", {
                              name: booking.noShowByUserName,
                              at: formattedNoShow,
                            })
                          : t("bookings.details.noShowFlaggedAtGuest", {
                              at: formattedNoShow,
                            })}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 pt-2">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        className="flex-1 rounded-full"
                      >
                        {t("bookings.details.close")}
                      </Button>
                      {canCancel && booking.status === "BOOKED" && (
                        <Button
                          onClick={() => setConfirmCancelOpen(true)}
                          className="flex-1 rounded-full bg-red-600 hover:bg-red-700 text-white"
                        >
                          {t("bookings.details.cancelBooking")}
                        </Button>
                      )}
                    </div>
                    {canEdit && booking.status === "BOOKED" && (
                      <Button
                        variant="outline"
                        onClick={() => setView("reschedule")}
                        className="w-full rounded-full"
                      >
                        {t("bookings.details.rescheduleBooking")}
                      </Button>
                    )}
                    {canCancel && booking.status === "BOOKED" && (
                      <Button
                        variant="outline"
                        onClick={() => noShowMutation.mutate(!booking.noShow)}
                        disabled={noShowMutation.isPending}
                        className={`w-full rounded-full ${
                          booking.noShow
                            ? "text-neutral-600 border-neutral-200 hover:bg-neutral-50"
                            : "text-amber-700 border-amber-200 hover:bg-amber-50"
                        }`}
                      >
                        {noShowMutation.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          t(
                            booking.noShow
                              ? "bookings.details.unflagNoShow"
                              : "bookings.details.flagNoShow",
                          )
                        )}
                      </Button>
                    )}
                    {canCancel &&
                      booking.status === "BOOKED" &&
                      booking.seriesId && (
                        <Button
                          variant="outline"
                          onClick={() => setConfirmCancelSeriesOpen(true)}
                          className="w-full rounded-full text-red-600 border-red-200 hover:bg-red-50"
                        >
                          {t("bookings.details.cancelSeries")}
                        </Button>
                      )}
                  </div>
                </>
              )}
            </>
          )}
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      <AlertDialog open={confirmCancelOpen} onOpenChange={setConfirmCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("bookings.details.cancelTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("bookings.details.cancelDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelMutation.isPending}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={cancelMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                cancelMutation.mutate();
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {cancelMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("bookings.details.cancelBooking")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmCancelSeriesOpen}
        onOpenChange={setConfirmCancelSeriesOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("bookings.details.cancelSeriesTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("bookings.details.cancelSeriesDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelSeriesMutation.isPending}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={cancelSeriesMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (booking?.seriesId)
                  cancelSeriesMutation.mutate(booking.seriesId);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {cancelSeriesMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("bookings.details.cancelSeries")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function DetailRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-neutral-500">{label}</p>
        <p className="font-medium text-neutral-800 break-words">{value}</p>
      </div>
    </div>
  );
}

// Renders the extra players on a booking. For tennis/padel doubles (3 names)
// the first slot is the partner; the rest are numbered opponents. Singles
// (1 name) is a single opponent. Any other sport: a labelled list.
function RosterRow({
  players,
  sportName,
}: {
  players: { name: string; position: number }[];
  sportName: string;
}) {
  const { t } = useTranslation();
  const isFormatSport = sportName === "TENNIS" || sportName === "PADEL";
  const sorted = [...players].sort((a, b) => a.position - b.position);

  const rows: { label: string; name: string }[] = [];
  if (isFormatSport && sorted.length === 3) {
    rows.push({
      label: t("bookings.details.rosterPartner"),
      name: sorted[0].name,
    });
    rows.push({
      label: t("bookings.details.rosterOpponentN", { index: 1 }),
      name: sorted[1].name,
    });
    rows.push({
      label: t("bookings.details.rosterOpponentN", { index: 2 }),
      name: sorted[2].name,
    });
  } else if (isFormatSport && sorted.length === 1) {
    rows.push({
      label: t("bookings.details.rosterOpponent"),
      name: sorted[0].name,
    });
  } else {
    sorted.forEach((p, i) => {
      rows.push({
        label: t("bookings.details.rosterPlayerN", { index: i + 1 }),
        name: p.name,
      });
    });
  }

  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5">
        <Users className="w-4 h-4 text-primary-500" />
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <p className="text-xs text-neutral-500">
          {t("bookings.details.roster")}
        </p>
        <ul className="space-y-0.5">
          {rows.map((r, i) => (
            <li
              key={i}
              className="font-medium text-neutral-800 break-words text-sm"
            >
              <span className="text-neutral-500 font-normal">{r.label}:</span>{" "}
              {r.name}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
